import type { Permission, Provider, Role } from '@natashu/shared';
import { addDaysToDateString, zonedDateString, zonedParts, zonedWallTimeToUtc } from '@natashu/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, ApiError, buildUrl, isApiError, NETWORK_ERROR_MESSAGE, type QueryParams } from '../../lib/api.ts';

/* ------------------------------------------------------------------ shared */

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

/**
 * Query keys. Lists that realtime already invalidates keep their prefixes
 * (`notifications`, `connections`); the rest are refreshed after mutations.
 */
export const adminKeys = {
  users: (params?: QueryParams) => (params ? (['users', 'list', params] as const) : (['users'] as const)),
  user: (id: string) => ['user', id] as const,
  userCatalog: ['users', 'catalog'] as const,
  settings: ['settings'] as const,
  connections: ['connections'] as const,
  folders: ['folders', 'tree', 'admin'] as const,
  events: ['events', 'list', 'admin'] as const,
  divisions: (eventId: string) => ['event', eventId, 'divisions'] as const,
  notifCatalog: ['notifications', 'catalog'] as const,
  destinations: ['notifications', 'destinations'] as const,
  rules: ['notifications', 'rules'] as const,
  deliveries: (params: QueryParams) => ['notifications', 'deliveries', params] as const,
  knowledge: (params?: QueryParams) => (params ? (['knowledge', params] as const) : (['knowledge'] as const)),
  aiPolicies: ['ai-policies'] as const,
  aiRuns: (params: QueryParams) => ['ai-runs', params] as const,
  audit: (params?: QueryParams) => (params ? (['audit', params] as const) : (['audit'] as const)),
  auditEntity: (type: string, id: string) => ['audit', 'entity', type, id] as const,
  reportsDashboard: ['reports', 'dashboard'] as const,
  reportsAgents: (days: number) => ['reports', 'agents', days] as const,
  reportsPayments: (scope: string) => ['reports', 'payments', scope] as const,
};

/** Keyset list with "โหลดเพิ่ม". */
export function useKeysetList<T>(key: readonly unknown[], path: string, params: QueryParams, enabled = true) {
  const query = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam, signal }) => api.get<Page<T>>(path, { query: { ...params, cursor: pageParam }, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<T>) => last.nextCursor,
    enabled,
  });
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items), [query.data]);
  return { ...query, rows };
}

const THAI = /[฀-๿]/;

/** Field path → Thai message from a VALIDATION_FAILED response. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!isApiError(err, 'VALIDATION_FAILED')) return {};
  const issues = (err.details as { issues?: Array<{ path: string; message: string }> } | null)?.issues ?? [];
  const out: Record<string, string> = {};
  for (const issue of issues) {
    const k = issue.path || '_';
    if (!out[k]) out[k] = THAI.test(issue.message) ? issue.message : 'ข้อมูลไม่ถูกต้อง';
  }
  return out;
}

export const VERSION_CONFLICT_TH = 'มีผู้อื่นแก้ไขข้อมูลนี้ก่อนคุณ ระบบโหลดข้อมูลล่าสุดแล้ว กรุณาตรวจสอบและบันทึกอีกครั้ง';

export function isVersionConflict(err: unknown): boolean {
  return isApiError(err, 'VERSION_CONFLICT');
}

export function isForbidden(err: unknown): boolean {
  return isApiError(err, 'FORBIDDEN');
}

/** micro-USD (integer) → "$0.0123" (4 decimals). */
export function formatMicroUsd(micro: number | string | null | undefined): string {
  if (micro === null || micro === undefined) return '—';
  const n = Number(micro);
  if (!Number.isFinite(n)) return '—';
  return `$${(n / 1_000_000).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

/* ------------------------------------------------------------------ time helpers (Asia/Bangkok) */

const pad = (n: number) => String(n).padStart(2, '0');

export function todayBangkok(): string {
  return zonedDateString(new Date());
}

/** ISO instant → value for <input type="datetime-local"> in Bangkok wall time. */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = zonedParts(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** <input type="datetime-local"> value (Bangkok wall time) → ISO instant. */
export function localInputToIso(value: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(value);
  if (!m) return null;
  return zonedWallTimeToUtc(m[1]!, m[2]!).toISOString();
}

/** Start of a Bangkok calendar day as ISO. */
export function dayStartIso(date: string): string {
  return zonedWallTimeToUtc(date, '00:00').toISOString();
}

/** Exclusive end of a Bangkok calendar day ("valid until the end of that day") as ISO. */
export function dayEndIso(date: string): string {
  return zonedWallTimeToUtc(addDaysToDateString(date, 1), '00:00').toISOString();
}

/** ISO start → Bangkok date string. */
export function isoToDay(iso: string | null | undefined): string {
  return iso ? zonedDateString(new Date(iso)) : '';
}

/** ISO exclusive end → the last Bangkok day it covers. */
export function isoEndToDay(iso: string | null | undefined): string {
  return iso ? zonedDateString(new Date(new Date(iso).getTime() - 1)) : '';
}

/* ------------------------------------------------------------------ CSV download */

/** GET a file with the session cookie and save it (real app, not an artifact sandbox). */
export async function downloadFile(path: string, query: QueryParams, fallbackName: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), { credentials: 'include', headers: { 'X-Requested-With': 'natashu', Accept: 'text/csv, application/json' } });
  } catch {
    throw new ApiError({ code: 'NETWORK', message: NETWORK_ERROR_MESSAGE, retryable: true, status: 0 });
  }
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string } } | null = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiError({
      code: (body?.error?.code as ApiError['code']) ?? 'INTERNAL',
      message: body?.error?.message ?? 'ดาวน์โหลดไม่สำเร็จ',
      retryable: false,
      status: res.status,
    });
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ------------------------------------------------------------------ users */

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: Role;
  roleLabelTh: string;
  isActive: boolean;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
  mustChangePassword: boolean;
  grantedPermissions: Permission[];
  revokedPermissions: Permission[];
  effectivePermissions: Permission[];
  version: number;
  createdAt: string;
}

export interface UserCatalog {
  roles: Array<{ key: Role; labelTh: string; hintTh: string; permissions: Permission[] }>;
  permissions: Array<{ key: Permission; labelTh: string }>;
}

export interface UserWithPassword {
  user: AdminUser;
  temporaryPassword: string | null;
  temporaryPasswordAlreadyShown?: boolean;
}

export function useUserCatalog(enabled = true) {
  return useQuery({
    queryKey: adminKeys.userCatalog,
    queryFn: ({ signal }) => api.get<UserCatalog>('/api/users/catalog', { signal }),
    staleTime: 10 * 60_000,
    enabled,
  });
}

/** All members (small team; first 200). */
export function useAllUsers(enabled = true) {
  return useQuery({
    queryKey: adminKeys.users({ limit: 200 }),
    queryFn: ({ signal }) => api.get<Page<AdminUser>>('/api/users', { query: { limit: 200 }, signal }),
    enabled,
    staleTime: 60_000,
  });
}

/* ------------------------------------------------------------------ settings */

export interface QuietHoursValue {
  start: string;
  end: string;
}

export interface WorkspaceSettingsValue {
  folderMaxDepth: number;
  agentsSeeUnassigned: boolean;
  businessHours: { enabled: boolean; days: Array<{ weekday: number; open: string; close: string }> };
  defaultQuietHours: QuietHoursValue | null;
  aiDefaultMode: 'off' | 'draft_only' | 'auto_reply';
  /** Optional menu modules; older servers may omit it. */
  modules?: { competition: boolean };
  /** ใบสมัคร → ตั้งค่ายืนยันการสมัคร (v1.31.0); older servers omit them. */
  registrationConfirmEnabled?: boolean;
  registrationConfirmMessage?: string;
  /** Images sent with that message (v1.36.0). */
  registrationConfirmMediaIds?: string[];
  /** Sent as its own message right after (v1.37.0) — easy for the customer to copy. */
  registrationConfirmExtra?: string;
  /** Platforms this message belongs in; null = all. LINE starts off — it contradicts itself there (v1.69.0). */
  registrationConfirmProviders?: string[] | null;
  /** บัญชีที่มีความสัมพันธ์ → ตั้งค่าข้อความยืนยันความสัมพันธ์ (v1.67.0); older servers omit them. */
  relatedConfirmEnabled?: boolean;
  relatedConfirmMessage?: string;
  relatedConfirmMediaIds?: string[];
  /** ตั้งค่าตำแหน่ง (v1.99.0): the field, sent as {แผนที่สนาม}. */
  /** "ข้อมูลทีมจากลิงก์ → ตั้งค่า" (v1.101.0): false = team links are read-only. */
  teamKitEditable?: boolean;
  /** Per-section switches (v1.102.0); a missing key = on. */
  teamKitSections?: Partial<Record<'info' | 'event' | 'place' | 'starters' | 'subs' | 'logo' | 'withdraw', boolean>>;
  venue?: { name: string; address: string; lat: number | null; lng: number | null };
  /** SMS (v1.80.0): what the operator refuses, and the brake the shop sets on its own spending. */
  smsBannedWords?: string[];
  smsDailyCap?: number;
  smsOwnDomains?: string[];
  frequencyCaps?: { maxOccurrencesPerContact: number; maxCampaignMessagesPerContactPerDay: number | null; minHoursBetweenCampaignMessages: number | null };
  retention?: { messagesDays: number | null; webhookEventsDays: number | null };
  notificationDefaults?: { debounceSeconds: number; digestMode: 'off' | 'hourly' | 'daily'; quietHours: QuietHoursValue | null; slaMinutes: number | null };
}

export interface SettingsResponse {
  workspaceId: string;
  name: string;
  timezone: string;
  version: number;
  canManage: boolean;
  settings: WorkspaceSettingsValue;
  /** Server-enforced cap on messages kept per conversation (0 = unlimited). Read-only. */
  messageRetentionPerConversation: number;
}

export function useSettings() {
  return useQuery({ queryKey: adminKeys.settings, queryFn: ({ signal }) => api.get<SettingsResponse>('/api/settings', { signal }) });
}

/* ------------------------------------------------------------------ connections / folders / events */

export interface ConnectionItem {
  id: string;
  provider: Provider;
  providerLabel: string;
  name: string;
  purpose: 'customer' | 'admin_notify' | 'both';
  purposeLabel: string;
  status: string;
  statusLabel: string;
  isDemo: boolean;
}

export function useConnections(enabled = true) {
  return useQuery({
    queryKey: adminKeys.connections,
    queryFn: ({ signal }) => api.get<Page<ConnectionItem>>('/api/connections', { signal }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export interface FolderItem {
  id: string;
  parentId: string | null;
  name: string;
  targetType: 'contact' | 'conversation' | 'registration';
  level: number;
  path?: string;
  archivedAt: string | null;
}

export function useFolders(enabled = true) {
  return useQuery({
    queryKey: adminKeys.folders,
    queryFn: ({ signal }) => api.get<{ items: FolderItem[] }>('/api/folders', { query: { counts: 'false' }, signal }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export interface EventItem {
  id: string;
  title: string;
  startsOn: string;
  status: string;
}

export interface DivisionItem {
  id: string;
  code: string;
  label: string;
}

export function useEvents(enabled = true) {
  return useQuery({
    queryKey: adminKeys.events,
    queryFn: ({ signal }) => api.get<Page<EventItem>>('/api/events', { query: { limit: 50 }, signal }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useDivisions(eventId: string | null) {
  return useQuery({
    queryKey: adminKeys.divisions(eventId ?? ''),
    queryFn: ({ signal }) => api.get<{ items: DivisionItem[] }>(`/api/events/${eventId}/divisions`, { signal }),
    enabled: Boolean(eventId),
    staleTime: 60_000,
    retry: false,
  });
}

/* ------------------------------------------------------------------ notifications */

export interface NotifCatalog {
  triggers: Array<{ key: string; labelTh: string; hintTh: string }>;
  deliveryStates: Array<{ key: string; th: string; hint: string; tone: string; icon: string }>;
  providers: string[];
  maxDebounceSeconds: number;
  verificationTtlMinutes: number;
}

export interface Destination {
  id: string;
  channelAccountId: string;
  channelAccountName: string;
  provider: Provider;
  providerLabel: string;
  isDemo: boolean;
  channelStatus: string;
  label: string;
  ownerUserId: string | null;
  verificationState: 'pending' | 'verified' | 'disabled';
  verificationLabelTh: string;
  verifiedAt: string | null;
  verificationExpiresAt: string | null;
  verificationExpired: boolean;
  target: string | null;
  version: number;
  createdAt: string;
  health: {
    status: 'ok' | 'failing' | 'no_data';
    lastSentAt: string | null;
    sent24h: number;
    deadCount: number;
    pendingCount: number;
    lastFailureAt: string | null;
    lastError: string | null;
  };
}

export interface Verification {
  code: string;
  expiresAt: string;
  deepLink: string | null;
  instructionsTh: string;
  provider: Provider;
}

export interface RuleConditions {
  providers?: string[];
  channelAccountIds?: string[];
  folderIds?: string[];
  divisionIds?: string[];
  assignee?: 'any' | 'me' | 'unassigned' | 'specific';
  assigneeUserId?: string;
  failureThreshold?: number;
  quotaPercent?: number;
}

export interface NotificationRule {
  id: string;
  name: string;
  trigger: string;
  triggerLabelTh: string;
  conditions: RuleConditions;
  destinationIds: string[];
  destinations: Array<{ id: string; label: string | null; verificationState: string }>;
  debounceSeconds: number;
  quietHours: QuietHoursValue | null;
  digestMode: 'off' | 'hourly' | 'daily';
  slaMinutes: number | null;
  enabled: boolean;
  mutedUntil: string | null;
  isMuted: boolean;
  version: number;
  updatedAt: string;
}

export interface NotificationDelivery {
  id: string;
  ruleId: string | null;
  ruleName: string | null;
  destinationId: string;
  destinationLabel: string;
  trigger: string;
  triggerLabelTh: string;
  state: string;
  stateLabelTh: string;
  digest: boolean;
  itemCount: number;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string | null;
  lastError: string | null;
  sentAt: string | null;
  isTest: boolean;
  retryable: boolean;
  createdAt: string;
}

export function useNotifCatalog() {
  return useQuery({ queryKey: adminKeys.notifCatalog, queryFn: ({ signal }) => api.get<NotifCatalog>('/api/notifications/catalog', { signal }), staleTime: 10 * 60_000 });
}

/** `pollWhile` returns true to keep polling every 5 s (e.g. while a destination awaits verification). */
export function useDestinations(pollWhile?: (items: Destination[] | undefined) => boolean) {
  return useQuery({
    queryKey: adminKeys.destinations,
    queryFn: ({ signal }) => api.get<Page<Destination>>('/api/notifications/destinations', { query: { limit: 200 }, signal }),
    refetchInterval: (q) => (pollWhile?.(q.state.data?.items) ? 5000 : false),
  });
}

export function useRules() {
  return useQuery({ queryKey: adminKeys.rules, queryFn: ({ signal }) => api.get<Page<NotificationRule>>('/api/notifications/rules', { query: { limit: 200 }, signal }) });
}

/* ------------------------------------------------------------------ AI & knowledge */

export interface KnowledgeItem {
  id: string;
  title: string;
  body: string;
  category: string | null;
  tags: string[];
  status: 'draft' | 'approved' | 'archived';
  validFrom: string | null;
  validUntil: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  createdBy: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  isCurrentlyValid: boolean;
  usableByAi: boolean;
}

export type AiMode = 'off' | 'draft_only' | 'auto_reply';
export type AutoReplyCategory = 'faq' | 'availability' | 'registration_status';

export interface AutoReplyConfig {
  enabled: boolean;
  confidenceThreshold: number;
  allowedCategories: AutoReplyCategory[];
  maxPerConversationPerHour: number;
  activeHours: { start: string; end: string; days?: number[] } | null;
}

export interface AiPolicy {
  id: string;
  scopeType: 'workspace' | 'channel_account' | 'folder';
  scopeId: string | null;
  mode: AiMode;
  config: { autoReply?: AutoReplyConfig; effort?: 'low' | 'medium' | 'high' };
  updatedBy: string | null;
  version: number;
  updatedAt: string;
}

export interface AiPoliciesResponse {
  items: AiPolicy[];
  defaultMode: AiMode;
  resolutionOrder: string[];
}

export interface AiUsageRow {
  day: string;
  model: string;
  kind: string;
  runs: number;
  inputTokens: number | string;
  outputTokens: number | string;
  cacheReadTokens: number | string;
  cacheWriteTokens: number | string;
  costMicroUsd: number | string;
}

export interface AiRunsReport {
  from: string;
  to: string;
  totals: { runs: number; inputTokens: number | string; outputTokens: number | string; costMicroUsd: number | string; unpricedRuns: number } | null;
  usage: AiUsageRow[];
  outcomes: Array<{ outcome: string; count: number }>;
  pricesCheckedAt: string;
}

export interface AiStatusFull {
  configured: boolean;
  model: string;
  defaultModel: string;
  defaultMode: AiMode;
  health: string;
  lastErrorAt: string | null;
  refusalFallback: boolean;
  pricing?: { checkedAt: string; modelPriced: boolean };
}

export function useAiStatusFull() {
  return useQuery({ queryKey: ['ai-status', 'full'], queryFn: ({ signal }) => api.get<AiStatusFull>('/api/ai/status', { signal }), staleTime: 60_000 });
}

/* ------------------------------------------------------------------ audit */

export interface AuditEntry {
  id: number;
  createdAt: string;
  actorType: string;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  correlationId: string | null;
  ip: string | null;
}

/* ------------------------------------------------------------------ reports */

export interface FirstResponse {
  medianMinutes: number | null;
  p90Minutes: number | null;
  answered: number;
  waiting: number;
}

export interface DashboardReport {
  generatedAt: string;
  today: string;
  scope: { conversations: 'all' | 'visible'; payments: boolean };
  conversations: { needsReply: number; oldestWaitingMinutes: number | null; unassigned: number; inboundToday: number };
  firstResponse: { today: FirstResponse; last7Days: FirstResponse };
  inboundSeries: Array<{ date: string; count: number }>;
  payments: {
    pendingSlips: { count: number; withSlip: number; totalMinor: number | null; currency: string | null; byCurrency: Array<{ currency: string; count: number; totalMinor: number | string }> };
    outstandingByEvent: Array<{ eventId: string; title: string; startsOn: string; currency: string; registrations: number; withOutstanding: number; outstandingMinor: number | string }>;
  } | null;
  registrationsByEvent: Array<{ eventId: string; title: string; startsOn: string; byStatus: Record<string, number>; total: number }>;
  campaignSendsToday: { accepted: number; failed: number; unknown: number };
  unknownDeliveries: number;
}

export interface AgentsReport {
  days: number;
  scope: { conversations: 'all' | 'visible' };
  items: Array<{
    userId: string;
    displayName: string;
    role: Role;
    roleLabelTh: string;
    isActive: boolean;
    handledConversations: number;
    replies: number;
    firstResponse: { medianMinutes: number | null; p90Minutes: number | null; answered: number };
    openAssigned: number;
  }>;
}

export interface PaymentTotals {
  registrations: number;
  dueMinor: number | string;
  verifiedMinor: number | string;
  pendingMinor: number | string;
  outstandingMinor: number | string;
  depositMetCount: number;
  depositRequiredCount: number;
  paidCount: number;
}

export interface PaymentsReport {
  scope: string;
  items: Array<{
    eventId: string;
    title: string;
    startsOn: string;
    status: string;
    divisions: Array<PaymentTotals & { divisionId: string; code: string; label: string; currency: string }>;
    totalsByCurrency: Record<string, PaymentTotals>;
  }>;
}

export function useDashboardReport(enabled = true) {
  return useQuery({
    queryKey: adminKeys.reportsDashboard,
    queryFn: ({ signal }) => api.get<DashboardReport>('/api/reports/dashboard', { signal }),
    enabled,
    refetchInterval: 60_000,
  });
}
